import { createContext, useContext, useLayoutEffect, useState, type ReactNode } from 'react'

interface ThemeChangeEvent {
  stopPropagation?: () => void
}

interface ThemeContextValue {
  theme: string
  setTheme: (theme: string) => ((event?: ThemeChangeEvent | null) => void) | null
}

interface ThemeProviderProps {
  children?: ReactNode
  defaultTheme?: string
  storageKey?: string
}

const initialState: ThemeContextValue = {
  theme: 'system',
  setTheme: () => null,
}

const ThemeProviderContext = createContext<ThemeContextValue>(initialState)

function resolveSystemTheme(): string {
  try {
    return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light'
  } catch {
    return 'light'
  }
}

function readThemeFromStorage(storageKey: string, fallbackTheme: string): string {
  try {
    return localStorage.getItem(storageKey) || fallbackTheme
  } catch {
    return fallbackTheme
  }
}

function writeThemeToStorage(storageKey: string, theme: string): void {
  try {
    localStorage.setItem(storageKey, theme)
  } catch {
    // Browser storage can be unavailable in restricted Safari contexts.
  }
}

export function ThemeProvider({
  children,
  defaultTheme = 'system',
  storageKey = 'vite-ui-theme',
  ...props
}: ThemeProviderProps): ReactNode {
  const [theme, setTheme] = useState(() => {
    let currentTheme = readThemeFromStorage(storageKey, defaultTheme)
    if (currentTheme === 'system') {
      currentTheme = resolveSystemTheme()
    }
    return currentTheme
  })

  useLayoutEffect(() => {
    const root = window.document.documentElement

    root.classList.remove('light', 'dark')

    // Keep the DOM theme class in sync before child effects read CSS variables.
    root.classList.add(theme)
  }, [theme])

  const value = {
    theme,
    setTheme: (theme: string) => (e?: ThemeChangeEvent | null) => {
      e?.stopPropagation?.()
      writeThemeToStorage(storageKey, theme)
      setTheme(theme)
    },
  }

  return (
    <ThemeProviderContext.Provider {...props} value={value}>
      {children}
    </ThemeProviderContext.Provider>
  )
}

export const useTheme = (): ThemeContextValue => {
  const context = useContext(ThemeProviderContext)

  if (context === undefined) throw new Error('useTheme must be used within a ThemeProvider')

  return context
}
